import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const isSupabaseConfigured = !!(supabaseUrl && supabaseKey);

export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: false,
      },
    })
  : null;

export const SB = isSupabaseConfigured;

/**
 * Upload a compressed image blob to Supabase Storage.
 * Returns the public URL or null on failure.
 */
export async function uploadToStorage(userId, blob, filename) {
  if (!supabase) { console.warn('Storage upload skipped: Supabase not configured'); return null; }
  try {
    const safeName = filename.replace(/[^a-z0-9._-]/gi, '_');
    const path = `${userId}/${Date.now()}_${safeName}`;
    const { data, error } = await supabase.storage
      .from('profile-photos')
      .upload(path, blob, { contentType: 'image/jpeg', upsert: false });
    if (error) {
      console.error('Storage upload error:', error.message, '| Bucket: profile-photos | Path:', path);
      return null;
    }
    const { data: urlData } = supabase.storage.from('profile-photos').getPublicUrl(data.path);
    return urlData.publicUrl;
  } catch (e) {
    console.error('Storage upload exception:', e);
    return null;
  }
}

/**
 * Upload a captured selfie blob to the dedicated selfie bucket.
 * Returns the storage path and public URL when successful.
 */
export async function uploadSelfieToStorage(userId, blob, filename) {
  if (!supabase) { console.warn('Selfie upload skipped: Supabase not configured'); return null; }
  try {
    const safeName = filename.replace(/[^a-z0-9._-]/gi, '_');
    const path = `${userId}/${Date.now()}_${safeName}`;
    const { data, error } = await supabase.storage
      .from('profile-selfies')
      .upload(path, blob, { contentType: 'image/jpeg', upsert: false });
    if (error) {
      console.error('Selfie upload error:', error.message, '| Bucket: profile-selfies | Path:', path);
      return null;
    }
    const { data: urlData } = supabase.storage.from('profile-selfies').getPublicUrl(data.path);
    return { path: data.path, publicUrl: urlData.publicUrl };
  } catch (e) {
    console.error('Selfie upload exception:', e);
    return null;
  }
}

/**
 * Delete all files from the user's folder in the profile-photos bucket.
 * Uploads are stored as `${userId}/filename`, so we can list and remove by prefix.
 */
export async function deleteUserPhotosFromStorage(userId) {
  if (!supabase || !userId) return { success: true, deleted: 0 };

  try {
    const bucket = supabase.storage.from('profile-photos');
    const paths = [];
    let offset = 0;

    while (true) {
      const { data, error } = await bucket.list(userId, {
        limit: 100,
        offset,
        sortBy: { column: 'name', order: 'asc' },
      });

      if (error) return { success: false, error: error.message || 'Failed to list storage files' };
      if (!data || data.length === 0) break;

      data.forEach(file => {
        if (file?.name) paths.push(`${userId}/${file.name}`);
      });

      if (data.length < 100) break;
      offset += data.length;
    }

    if (paths.length === 0) return { success: true, deleted: 0 };

    const { error: removeError } = await bucket.remove(paths);
    if (removeError) return { success: false, error: removeError.message || 'Failed to remove storage files' };

    return { success: true, deleted: paths.length };
  } catch (e) {
    return { success: false, error: e?.message || 'Storage cleanup failed' };
  }
}
